import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduce, each, delay, find, replace, startsWith, camelCase } from 'lodash';
import PropTypes from 'prop-types';
import ExcelJS from 'exceljs';
import pluralize from 'pluralize';
import accounting from 'accounting';
import ReactHighcharts from 'react-highcharts';
import Fa from 'react-fontawesome';
import { ButtonGroup, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ProgressBar from '../../../../components/progress-bar';
import { 
  getProgressLoadPercentage,
  getProgressStats,
} from '../../../Dashboard/DashboardReducer';
import { 
  recalculateAccountSizeStats,
} from '../../../Dashboard/DashboardActions';
import NumberDisplay from '../../../../components/number-display';
import Counter from '../../../../components/counter';
import { getHasNoDecisionSetup } from '../../../../components/search-form/search-form-reducer';
import DropdownRange from '../../../../components/dropdown-range';


import styles from './styles.css';

const STATS = [
  { key: 'notQualified', title: 'Not Qualified', color: '#ECC048' },
  { key: 'submitted', title: 'Submitted', color: '#7641B4' },
  { key: 'notSubmitted', title: 'Not Submitted', color: '#E58D5E' },
  { key: 'excluded', title: 'Excluded', color: '#3266A1' },
];

const DICTIONARY = {
  won: 'Wins',
  lost: 'Losses',
  noDecision: 'No Decisions',

  hasFeedbacks: 'Provided Feedback',
  // hasRequests: 'Requesting Feedback',
  inOutreach: 'Requesting Feedback',
  hasBounced: 'Request Feedback Bounced',
  hasNonResponsive: 'No Response',
  hasRefused: 'Opted Out',
  excluded: 'Excluded',
  
  noQualifiedContacts: 'No Qualified Contact(s)',
  preventedByCollectorSettings: 'Prevented by Collector Settings',
  failedProductConditions: 'Failed Product Conditions',
  invalidOwners: 'Invalid Owners',
  invalidSenders: 'Invalid Senders',
  invalidAccounts: 'Invalid Accounts',
  others: 'Others',

  renewal: 'Renewal',
  newBusiness: 'New Business',
  addOn: 'Add-on',
  upSell: 'Upsell',

  small: 'Small',
  medium: 'Medium',
  large: 'Large',
  
}

const ANNUAL_REVENUE_MIN = 'PROGRESS_ANNUAL_REVENUE_MIN';
const ANNUAL_REVENUE_MAX = 'PROGRESS_ANNUAL_REVENUE_MAX';

class ProgressSummary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      viewType: 'outcome',
      loading: false,
    };
    this.onToggleViewType = this.onToggleViewType.bind(this);
    this.onSliderChange = this.onSliderChange.bind(this);
    this.onExportExcel = this.onExportExcel.bind(this);
  }



  getSizeRange(props) {
    const { userPreferences } = this.context;
    const { progressStats } = props;
    let revenueMin = 0;
    if (userPreferences[ANNUAL_REVENUE_MIN] !== undefined) {
      revenueMin = userPreferences[ANNUAL_REVENUE_MIN];
    } else {
      revenueMin = progressStats.defaultBounds?.[0] || 0;
    }

    let revenueMax = 0;
    if (userPreferences[ANNUAL_REVENUE_MAX] !== undefined) {
      revenueMax = userPreferences[ANNUAL_REVENUE_MAX];
    } else {
      revenueMax = progressStats.defaultBounds?.[1] || 0;
    }
    return [revenueMin, revenueMax];
  }

  onSliderChange(bounds) {
    const [revenueMin, revenueMax] = bounds;
    this.setState({ loading: true }, () => {
      const p1 = this.context.updateUserPreference(ANNUAL_REVENUE_MIN, revenueMin);
      const p2 = this.context.updateUserPreference(ANNUAL_REVENUE_MAX, revenueMax);
      Promise.all([p1, p2])
        .then(
          () => {
            this.props.dispatch(recalculateAccountSizeStats(revenueMin, revenueMax));
            delay(() => {
              this.setState({ loading: false });
            }, 500);
          },
          () => {
            this.setState({ loading: false });
          },
        );  
    });
    
  }

  isLoading() {
    const { loadPercentage } = this.props;
    return loadPercentage > 0 && loadPercentage < 1;
  }
  
  isLoaded() {
    const { loadPercentage } = this.props;
    return loadPercentage === 1;
  }


  isAccount() {
    return this.props.automation.source === 'account';
  }


  onToggleViewType() {
    const { viewType } = this.state;
    this.setState({ viewType: viewType === 'outcome' ? 'dealType' : 'outcome' });
  }

  onExportExcel() {
    const { progressStats, hasNoDecision } = this.props;
    const isAccount = this.isAccount();
    const stages = ['won', 'lost', ...(hasNoDecision ? ['noDecision'] : [])];
    const dealTypes = ['renewal', 'newBusiness', 'addOn', 'upSell'];
    const sizes = ['small', 'medium', 'large'];
    
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'TruVoice Automation';
    workbook.created = new Date();
    workbook.modified = new Date();


    const sheets = [];
    if (isAccount) {
      sheets.push({
        name: 'Account Size',
        header: 'All Sizes',
        attrs: sizes,
      });
    } else {
      sheets.push({
        name: 'Outcomes',
        header: 'All Outcomes',
        attrs: stages,
      });
      sheets.push({
        name: 'Deal Types',
        header: 'All Deal Types',
        attrs: dealTypes,
      });
    }

    each(sheets, (sheet) => {
      const { attrs } = sheet;

      const worksheet = workbook.addWorksheet(sheet.name);

      worksheet.columns = [
        { header: '', key: 'title', width: 50 },
        { header: sheet.header, key: 'total', width: 20 },
        ...(reduce(attrs, (items, attr) => {
          items.push({
            header: DICTIONARY[attr] || attr,
            key: attr,
            width: 20,
          });
          return items;
        }, [])),
      ];
  
      each([{ key: 'all', title: isAccount ? 'All Accounts' : 'All Opportunities' }, ...STATS], (st, c) => {
        const mainKey = st.key;
        const allKeys = [mainKey];
        each(progressStats, (ps, psK) => {
          if (startsWith(psK, `${mainKey}/`)) {
            allKeys.push(psK);
          }
        });
  
        each(allKeys, (k) => {
          const isMain = mainKey === k;
          const isExcluded = mainKey === 'excluded';
  
          const progressStat = progressStats[k];
          const rawTitle = replace(k, `${mainKey}/`, '');
  
          let subTitle = DICTIONARY[rawTitle] || rawTitle;
          if (isExcluded) {
            subTitle = progressStat.label || progressStat.reason || subTitle;
          }
          if (progressStat) {
            if (isMain || progressStat.count > 0) {
              worksheet.addRow({
                title: isMain ? st.title : `   > ${subTitle}`,
                total: progressStat.count || 0,
                ...(reduce(attrs, (items, attr) => {
                  if (progressStat[attr]) {
                    items[attr] = progressStat[attr].count;
                  }
                  return items;
                }, {})),
              });
            }

          }
        });
        if (c < STATS.length) {
          worksheet.addRow({
            title: '',
          });
        }
      });
    });
    workbook.xlsx.writeBuffer()
      .then(
        (data) => {
          const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8' });
          const a = document.createElement('a');
          document.body.appendChild(a);
          a.setAttribute('style', 'display: none');
          const url = window.URL.createObjectURL(blob);
          a.href = url;
          a.download = `${isAccount ? 'Accounts' : 'Opportunities'} by Submission Stats.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);          
          document.body.removeChild(a);
        },
      );

    

  }

  renderPercentage() {
    const { loadPercentage } = this.props;
    const percentage = loadPercentage * 100;
    return (
      <span className={`text-muted animated ${loadPercentage === 1 ? 'fadeOut' : 'flash infinite'}`}>
        <Fa name="spinner" spin />
        {' '}
        <Counter value={percentage}>
          <NumberDisplay value={percentage} suffix="%" precision={percentage === '100' ? 0 : 1} />
        </Counter>
      </span>
    );
  }

  renderOverviewStats() {
    const { progressStats, automation } = this.props;
    const isEmpty = automation.is_empty === true;

    const isLoading = this.isLoading();
    return (
      <div>
        <div>
          {this.renderGraph()}
        </div>
        <div>
          <table className="table table-condensed">
            <tbody>
              {STATS.map((s) => {
                if (s.key !== 'notQualified') {
                  return this.renderRow(s.title, progressStats[s.key], s.key, s.color);
                }
                return null;
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }


  renderCickable(stats, innerComp) {
    const isLoading = this.isLoading();
    if (isLoading || stats.count === 0 || !stats.count) {
      return innerComp;
    }
    const handler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.props.showStats(stats.ids);
    };
    return (
      <a href="" onClick={handler}>{innerComp}</a>
    );
  }

  renderRow(title, stats, k, color) {
    const { progressStats, resources } = this.props;

    const totalStats = progressStats.notSubmitted || {};
    const total = resources?.length || 0;
    
    const count = stats && stats.count ? stats.count : 0;
    // if (count === 0 || !count) {
      // return null;
    // }
    const percentage = total !== 0 ? (count / total) * 100 : 0;

    let titleComp = title;
    let counterComp = (
      <Counter value={count}>
        <NumberDisplay value={count} precision={0} />
      </Counter>
    );
    let percentageComp = percentage === 0 ? '0%' : (
      <Counter value={percentage}>
        <NumberDisplay value={percentage} suffix="%" precision={2} />
      </Counter>
    );


    return (
      <tr key={k}>
        <td align="center" width="20" style={{ color }}><Fa name="square" /></td>
        <th>{this.renderCickable(stats, titleComp)}</th>
        <td width="100" className="text-right">{this.renderCickable(stats, counterComp)}</td>
        <td width="100" className="text-right">{percentageComp}</td>
      </tr>
    );
  }


  renderGraph() {
    const { progressStats } = this.props;
    const isAccount = this.isAccount();
    const data = [];
    let total = 0;
    each(progressStats, (progressStat, k) => {
      const st = find(STATS, { key: k });
      if (st) {
        total += progressStat.count;
        data.push({
          name: st?.title || k,
          y: progressStat.count,
          color: st?.color,
          // amount: stat.amount,
          // total: stat.total,
          opportunityIds: progressStat.ids,
          select: false,
          events: {
            click: (event) => {
              this.props.showStats(event.point.options.opportunityIds);
            },
          },
        });
      }
    });

    const config = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        type: 'pie',
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        formatter: function format() {
          const percentage = accounting.formatNumber(this.percentage, 2);
          const k = this.key;
          const c = accounting.formatNumber(this.y, 0);
          const amount = `${accounting.formatNumber(c, 0)} ${pluralize(isAccount ? 'account' : 'opportunity', this.y)}`;
          return `<div><p>${k}</p><b>${amount}</b> (${percentage}%)</div>`;
        },

        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'rgba(0, 0, 0, 0.25)',
        useHTML: true,
        style: {
          color: 'rgba(100, 100, 100, 0.75)',
          fontSize: '12px',
        },
      },
      title: total === 0 ? false : {
        text: `${accounting.formatNumber(total, 0)}`,
        verticalAlign: 'middle',
        y: 5,
        style: {
          color: '#333333',
          fontSize: '50px',
          fontWeight: '700',
        },
      },
      subtitle: {
        text: pluralize(isAccount ? 'Account' : 'Opportunity', total),
        verticalAlign: 'middle',
        y: 45,
        style: {
          color: 'rgba(0, 0, 0, 0.75)',
          fontSize: '20px',
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          shadow: false,
          borderWidth: 0,
          animation: {
            duration: 1000,
          },
          dataLabels: {
            enabled: false,
          },
        },
      },
      series: [
        {
          name: 'Submision Status',
          colorByPoint: true,
          innerSize: '80%',
          data,
        },
      ],
    };
    return (
      <ReactHighcharts config={config} ref="progress-chart" />
    );
  }

  renderCount(stats, k, total, bold = false) {
    const cmp = (
      <Counter value={stats.count}>
        <NumberDisplay value={stats.count} precision={0} />
      </Counter>
    );
    const per = total !== 0 && stats.count !== 0 ? (stats.count / total) * 100 : null;


    const percentage = per ? (
      <span className={`text-muted ${styles.percentage}`}>
        (
          <Counter value={per}>
            <NumberDisplay value={per} precision={per !== 100 ? 2 : 0} suffix="%" />
          </Counter>
        )
      </span>
    ) : null;
    const inner = this.renderCickable(stats, cmp);
    return [
      (
        <td key={`${k}-main`} className="text-right">
          {bold && <strong>{inner}</strong>}
          {!bold && inner}
        </td>
      ),
      (
        <td key={`${k}-sub`} className="text-left" style={{ paddingRight: '15px' }}>
          {percentage}
        </td>
      )
    ];
  }

  renderDetailedStats() {
    const { viewType } = this.state;

    const { progressStats, hasNoDecision, automation } = this.props;
    const isEmpty = automation.is_empty === true;
    const isAccount = this.isAccount();

    const tbodies = [];
    const stages = ['won', 'lost'];
    if (hasNoDecision) {
      stages.push('noDecision');
    }
    let attrs = viewType === 'outcome' ? stages : ['renewal', 'newBusiness', 'addOn', 'upSell'];
    if (isAccount) {
      attrs = ['small', 'medium', 'large'];
    }


    const grandTotal = progressStats.all?.count || 0;

    each([{ key: 'all', title: isAccount ? 'All Accounts' : 'All Opportunities' }, ...STATS], (st, c) => {
      const mainKey = st.key;
      const allKeys = [mainKey];
      const isExcluded = mainKey === 'excluded';
      const isNotSubmitted = mainKey === 'notSubmitted';
      const isNotQualified = mainKey === 'notQualified';

      let lastRow = null; // Excluded, Other category shoul;d be display last
      
      each(progressStats, (ps, psK) => {
        if (startsWith(psK, `${mainKey}/`)) {
          if (isExcluded && ps.type === 'OTHER') {
            lastRow = psK;
          } else {
            allKeys.push(psK);
          }
        }
      });

      if (lastRow) {
        allKeys.push(lastRow); // make it display last
      }

      const rows = [];
      let total = 0;
      each(allKeys, (k) => {
        if (mainKey === k) {
          const progressStat = progressStats[k];
          total += progressStat.count || 0;
        }
      });

      each(allKeys, (k) => {
        const isMain = mainKey === k;
        const isExcluded = mainKey === 'excluded';

        const progressStat = progressStats[k];
        const rawTitle = replace(k, `${mainKey}/`, '');

        let subTitle = DICTIONARY[rawTitle] || rawTitle;
        if (isExcluded) {
          subTitle = progressStat.label || progressStat.reason || subTitle;
        }
        if (progressStat) {
          let note = null;
          if (k === 'all') {
            const tip = (
              <Tooltip id="opp-percentage">
                Because opportunities can have multiple contacts submitted for feedback, an opportunity can fall into more than one category. When this happens, percentages will be over 100%.
              </Tooltip>
            );
            note = (
              <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={tip}>
                <Fa name="info-circle" className="text-active" />
              </OverlayTrigger>
            );
          }
          const row = (
            <tr key={k}>
              {!isMain && <th>&nbsp;</th>}
              {isMain && <th colSpan="2">{st.title} {note}</th>}
              {!isMain && <td>{subTitle}</td>}
              
              {this.renderCount(progressStat, 'all', isMain ? grandTotal : total, isMain)}

              {attrs.map((att) => {
                if (progressStat[att]) {
                  return this.renderCount(progressStat[att], att, progressStat.count, isMain);
                }
                return (<td colSpan="2" key={att}>&nbsp;</td>);
              })}
            </tr>
          );

          if (isMain || progressStat.count > 0) {
            if (isEmpty) {
              if (!isNotQualified && (isMain || !isNotSubmitted)) {
                rows.push(row);  
              }
            } else {
              rows.push(row);
            }
          }
        }
      });




      const tbody = (
        <tbody key={mainKey} className={`${styles.tbody} ${styles[camelCase(`tbody ${mainKey}`)]}`}>
          {rows}
        </tbody>
      );
      tbodies.push(tbody);
      if (c < STATS.length && mainKey !== 'all') {
        tbodies.push((
          <tbody key={`${mainKey}-space`} className={styles.tbodyEmpty}><tr><th colSpan={(3 + attrs.length)}>&nbsp;</th></tr></tbody>
        ));
      }
    });

    let firstColumnName = null;
    if (isAccount) {
      firstColumnName = 'All Sizes';
    } else if (viewType === 'outcome') {
      firstColumnName = 'All Outcomes';
    } else if (viewType === 'dealType') {
      firstColumnName = 'All Deal Types';
    }


    return (
      <div>
        <table style={{ margin: 0 }} className={`table table-hover table-condensed ${styles.table}`}>
          <thead className={`${styles.thead}`}>
            <tr>
              <th colSpan="2">&nbsp;</th>
              <th className="text-center" colSpan="2">{firstColumnName}</th>
              {attrs.map((att) => {
                const colHeader = DICTIONARY[att] || att;
                return (
                  <th key={att} className="text-center" colSpan="2">{colHeader}</th>
                )
              })}
            </tr>
          </thead>
          {tbodies}
        </table>
      </div>
    );
  }

  renderToggle() {
    const { loadPercentage } = this.props;
    const { viewType, loading } = this.state;
    return [
      (
        <ButtonGroup key="main-buttons">
          <Button active={viewType === 'outcome'} onClick={this.onToggleViewType}>
            Outcome Type
          </Button>
          <Button active={viewType === 'dealType'} onClick={this.onToggleViewType}>
            Opportunity Type
          </Button>
  
        </ButtonGroup>
      ),
      (
        <Button key="download-button" onClick={this.onExportExcel} disabled={(loading || loadPercentage < 1)} bsStyle="link">
          <Fa name="download" />
        </Button>
      ),
    ];
  }

  renderSizeControl() {
    const { loading } = this.state;
    const { loadPercentage } = this.props;
    return (
      <span>
        Account size boundaries
        {' '}
        <DropdownRange
          bounds={this.getSizeRange(this.props)}
          onSliderChange={this.onSliderChange}
          isMoney
          disabled={(loading || loadPercentage < 1)}
          bsStyle="default"
          modalProps={{
            title: `Small, Medium and Large Annual Revenue Boundaries`,
            lowerLabel: 'Medium Size Start',
            upperLabel: 'Large Size Start',
          }}
        />
        {' '}

      
        <Button key="download-button" onClick={this.onExportExcel} disabled={(loading || loadPercentage < 1)} bsStyle="link">
          <Fa name="download" />
        </Button>
      
      </span>
    );
  }


  renderHeader() {
    const isAccount = this.isAccount();
    
    return (
      <div className="clearfix">
        <div className="pull-right">
          {isAccount ? this.renderSizeControl() : this.renderToggle()}
        </div>
        <h4 className="page-sub-header">
          Opportunities by Submission Status {this.renderPercentage()}
        </h4>
      </div>
    );
  }

  render() {
    const { loadPercentage, progressStats } = this.props;
    const header = this.renderHeader();

    if (loadPercentage === 0) {
      return (
        <div>
          {header}
          <p />
          <ProgressBar />
          <p />
        </div>
      );
    }
    return (
      <div>
        {header}
        <div className="row">
          <div className="col-md-4 col-lg-4">
            {this.renderOverviewStats()}
          </div>
          <div className="col-md-8 col-lg-8">
            {this.renderDetailedStats()}
          </div>
        </div>
      </div>
    )
  }
}

function mapStateToProps(state) {
  return {
    loadPercentage: getProgressLoadPercentage(state),
    progressStats: getProgressStats(state),
    hasNoDecision: getHasNoDecisionSetup(state),
  };
}


ProgressSummary.propTypes = {
  dispatch: PropTypes.func.isRequired,
  resources: PropTypes.arrayOf(PropTypes.shape({
    opportunity_id: PropTypes.string,
    account_id: PropTypes.string,
  })).isRequired,
  automation: PropTypes.shape({
    _id: PropTypes.string,
    is_empty: PropTypes.bool,
  }).isRequired,
  progressStats: PropTypes.shape({
  }).isRequired,
  loadPercentage: PropTypes.number,
  showStats: PropTypes.func,
  hasNoDecision: PropTypes.bool,
};
ProgressSummary.defaultProps = {
  loadPercentage: 0,
  progressStats: {},
  showStats: () => {},
  hasNoDecision: false,
};

ProgressSummary.contextTypes = {
  router: PropTypes.shape({}),
  updateUserPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.object.isRequired,
};


const comp = connect(mapStateToProps)(ProgressSummary);

comp.STATS = STATS;
comp.DICTIONARY = DICTIONARY;
comp.ANNUAL_REVENUE_MIN = ANNUAL_REVENUE_MIN;
comp.ANNUAL_REVENUE_MAX = ANNUAL_REVENUE_MAX;

export default comp;
